import React from 'react'
import { observer } from 'mobx-react-lite'

// evertel
import { Button, Col, Icon, Row, Text, ToggleSwitch } from '@evertel/web/ui'
import { useService } from '@evertel/di'
import { useUI } from '@evertel/web/ui'
import { CurrentUserController } from '@evertel/blue-user'
import { SessionService } from '@evertel/session/SessionService'
import { APIDataRoom } from '@evertel/types'


/* *****************************************************
    NOTE: this component is used in multiple locations.
    Make sure any edits are tested in all uses
***************************************************** */


interface RoomSettingsOptionsProps {
    room?: APIDataRoom
    options: Record<string, any>
    isSearchable: boolean
    isArchived?: boolean
    hideOptions?: string[]
    onChangeOption: (option: string, value: boolean) => void
    onOpenICModal?: () => void
    className?: string
}

const RoomSettingsOptions: React.FC<RoomSettingsOptionsProps> = observer(({
    room,
    options,
    isSearchable,
    isArchived,
    hideOptions,
    onChangeOption,
    onOpenICModal,
    ...otherProps
}) => {

    const currentUserController = useService(CurrentUserController, [])
    const sessionService = useService(SessionService, [])

    const { isConfirmed } = useUI()

    // When editing an existing room, use room.departmentId
    // When creating a new room, fall back to selectedDepartmentId
    const isExecutive = currentUserController.canExecutiveInDepartment(room?.departmentId ?? sessionService.selectedDepartmentId)

    const confirmAgencyWide = async (checked) => {
        if (checked) {
            // if turning on 'agency wide', warn them...
            const confirmed = await isConfirmed({
                title: 'Please Note!',
                message: 'Turning on this setting will add ALL verified members in your entire agency to this room. Are you sure you want to do this?'
            })

            if (confirmed) {
                onChangeOption('autoAddDepartmentMembers', true)
            }
        } else {
            onChangeOption('autoAddDepartmentMembers', false)
        }
    }

    const onBeforeArchive = async (checked: boolean) => {
        if (checked) {
            // if turning on 'archive', warn them...
            const confirmed = await isConfirmed({
                title: 'Are you sure you want to archive this room?',
                message: 'Archived rooms are no longer accessible by members. It can be unarchived by room managers or executives at any time.'
            })

            if (confirmed) {
                onChangeOption('isArchived', checked)
            }
        } else {
            onChangeOption('isArchived', checked)
        }
    }

    if (!options) return null

    return (
        <Col {...otherProps}>
            {/* PRIVATE */}
            {!hideOptions?.includes('isPrivate') &&
                <Row className="mt-2 pb-2 border-bottom">
                    <Col xs={1} align="center" valign="top" className="p-0">
                        <Icon name="lock" className="me-2" />
                    </Col>
                    <Col xs={9} className="ps-0">
                        <Text bold id="label-for-private-room-toggle">
                            Make Private:
                            <button
                                id="private-room-tooltip"
                                className="btn btn-link p-0 ms-1"
                                data-toggle="tooltip"
                                data-tooltip="Only invited users can access this room."
                                aria-label="Info: Only invited users can access this room."
                                data-tooltip-pos="top"
                                role="tooltip"
                            >
                                <Icon name="circle-info" color="primary" aria-hidden={true} />
                            </button>
                        </Text>
                    </Col>
                    <Col align="right">
                        <ToggleSwitch
                            name="departmentMembersCanJoinAsMember"
                            id={`departmentMembersCanJoinAsMember${room?.id ?? ''}`}
                            onChange={(checked) => onChangeOption('departmentMembersCanJoinAsMember', !checked)}
                            checked={!options.departmentMembersCanJoinAsMember}
                            disabled={isSearchable || options.autoAddDepartmentMembers || isArchived}
                            data-tooltip={(isSearchable)
                                ? 'Incident Command rooms cannot be marked private'
                                : (options.autoAddDepartmentMembers) ? 'Agency-Wide rooms cannot be marked private' : null}

                            data-tooltip-pos="left"
                            aria-describedby={isSearchable || options.autoAddDepartmentMembers ? 'private-disabled-reason' : undefined}
                            aria-labelledby="label-for-private-room-toggle"
                        />
                        <div id="private-disabled-reason" className="visually-hidden">
                            {isSearchable
                                ? 'Incident Command rooms cannot be marked private'
                                : options.autoAddDepartmentMembers
                                    ? 'Agency-Wide rooms cannot be marked private'
                                    : ''}
                        </div>
                    </Col>
                </Row>
            }
            {/* READ ONLY */}
            {!hideOptions?.includes('roomMembersCanMessage') &&
                <Row className="mt-2 pb-2 border-bottom">
                    <Col xs={1} align="center" valign="top" className="p-0">
                        <Icon name="eye" className="me-2" />
                    </Col>
                    <Col xs={9} className="ps-0">
                        <Text bold id="label-for-read-only-toggle">
                            Make Read Only:
                            <button
                                id="read-only-tooltip"
                                className="btn btn-link p-0 ms-1"
                                data-toggle="tooltip"
                                data-tooltip="Members can read but not post messages (only room managers can post)."
                                aria-label="Info: Members can read but not post messages (only room managers can post)."
                                data-tooltip-pos="top"
                                role="tooltip"
                            >
                                <Icon name="circle-info" color="primary" aria-hidden={true} />
                            </button>
                        </Text>
                    </Col>
                    <Col align="right">
                        <ToggleSwitch
                            name="roomMembersCanMessage"
                            id={`roomMembersCanMessage${room?.id ?? ''}`}
                            onChange={(checked) => onChangeOption('roomMembersCanMessage', !checked)}
                            checked={!options.roomMembersCanMessage}
                            disabled={isArchived}
                            aria-labelledby="label-for-read-only-toggle"
                        />
                    </Col>
                </Row>
            }
            {/* AGENCY WIDE */}
            {(!hideOptions?.includes('autoAddDepartmentMembers')) &&
                <Row className="mt-2 pb-2 border-bottom">
                    <Col xs={1} align="center" valign="top" className="p-0">
                        <Icon name="users" className="me-2" />
                    </Col>
                    <Col xs={9} className="ps-0">
                        <Text bold id="label-for-agency-wide-toggle">
                            Make Agency-Wide (Broadcast):
                            <button
                                id="agency-wide-tooltip"
                                className="btn btn-link p-0 ms-1"
                                data-toggle="tooltip"
                                data-tooltip="Automatically add ALL users (excluding guests) in the entire agency to this room."
                                aria-label="Info: Automatically add ALL users (excluding guests) in the entire agency to this room."
                                data-tooltip-pos="top-left"
                                role="tooltip"
                            >
                                <Icon name="circle-info" color="primary" aria-hidden={true} />
                            </button>
                        </Text>
                    </Col>
                    <Col align="right">
                        <ToggleSwitch
                            name="autoAddDepartmentMembers"
                            id={`autoAddDepartmentMembers${room?.id ?? ''}`}
                            onChange={(checked) => confirmAgencyWide(checked)}
                            checked={options.autoAddDepartmentMembers}
                            disabled={!options?.departmentMembersCanJoinAsMember || isSearchable || !isExecutive || isArchived}
                            data-tooltip={(!isExecutive) ? 'Only executives can edit this setting' :
                                (isSearchable) ? 'Incident Command rooms cannot also be Agency-Wide rooms' :
                                    (!options?.departmentMembersCanJoinAsMember) ? 'Private rooms cannot also be Agency-Wide rooms' : null}
                            data-tooltip-pos="left"
                            aria-labelledby="label-for-agency-wide-toggle"
                            aria-describedby="agency-wide-disabled-reason"
                        />
                        <div id="agency-wide-disabled-reason" className="visually-hidden">
                            {(!isExecutive) ? 'Only executives can edit this setting' :
                                (isSearchable) ? 'Incident Command rooms cannot also be Agency-Wide rooms' :
                                    (!options?.departmentMembersCanJoinAsMember) ? 'Private rooms cannot also be Agency-Wide rooms' : null}
                        </div>
                    </Col>
                </Row>
            }
            {/* OPEN/IC ROOMS */}
            {!hideOptions?.includes('isSearchable') &&
                <Row className="mt-2 pb-2 border-bottom">
                    <Col xs={1} align="center" valign="top" className="p-0">
                        <Icon name="bolt" className="me-2" />
                    </Col>
                    <Col xs={9} className="ps-0">
                        <Text bold id="label-for-incident-command-toggle">
                            Make Incident Command:
                            <button
                                id="incident-command-tooltip"
                                className="btn btn-link p-0 ms-1"
                                data-toggle="tooltip"
                                data-tooltip="IC rooms are discoverable by people in other agencies within the region you set. Great for true interoperability during crisis events."
                                aria-label="Info: IC rooms are discoverable by people in other agencies within the region you set. Great for true interoperability during crisis events."
                                data-tooltip-pos="top"
                                role="tooltip"
                            >
                                <Icon name="circle-info" color="primary" aria-hidden={true} />
                            </button>
                        </Text>
                        {(isSearchable && isExecutive && !hideOptions?.includes('accessCriteria') && !isArchived) &&
                            <Row>
                                <Button
                                    className="my-1"
                                    color="primary"
                                    size="sm"
                                    onClick={onOpenICModal}
                                >
                                    Set access criteria
                                </Button>
                            </Row>
                        }
                    </Col>
                    <Col align="right">
                        <ToggleSwitch
                            name="isSearchable"
                            id={`isSearchable${room?.id ?? ''}`}
                            onChange={(checked) => onChangeOption('isSearchable', checked)}
                            checked={isSearchable}
                            disabled={!options?.departmentMembersCanJoinAsMember || options.autoAddDepartmentMembers || !isExecutive || isArchived}
                            data-tooltip={(!isExecutive) ? 'Only executives can edit this setting' :
                                (options.autoAddDepartmentMembers) ? 'Agency-Wide rooms cannot also be Incident Command rooms' :
                                    (!options?.departmentMembersCanJoinAsMember) ? 'Private rooms cannot also be Incident Command rooms' : null}
                            data-tooltip-pos="left"
                            aria-labelledby="label-for-incident-command-toggle"
                        />
                    </Col>
                </Row>
            }
            {/* SCREENSHOT DETECTION */}
            {!hideOptions?.includes('detectScreenshots') &&
                <Row className="mt-2 pb-2 border-bottom">
                    <Col xs={1} align="center" valign="top" className="p-0">
                        <Icon name="camera-slash" className="me-2" />
                    </Col>
                    <Col xs={9} className="ps-0">
                        <Text bold id="label-for-screenshot-toggle">
                            Detect/Disable Screenshots on Mobile:
                            <button
                                id="detect-screenshots-tooltip"
                                className="btn btn-link p-0 ms-1"
                                data-toggle="tooltip"
                                data-tooltip="When enabled, screenshots will be prevented (Android) or a notice sent to the room when taken (iOS)"
                                aria-label="Info: When enabled, screenshots will be prevented (Android) or a notice sent to the room when taken (iOS)"
                                data-tooltip-pos="top-left"
                                role="tooltip"
                            >
                                <Icon name="circle-info" color="primary" aria-hidden={true} />
                            </button>
                        </Text>
                    </Col>
                    <Col align="right">
                        <ToggleSwitch
                            name="detectScreenshots"
                            id={`detectScreenshots${room?.id ?? ''}`}
                            onChange={(checked) => onChangeOption('detectScreenshots', checked)}
                            checked={(options.detectScreenshots === undefined) ? true : options.detectScreenshots}
                            disabled={isArchived}
                            aria-labelledby="label-for-screenshot-toggle"
                        />
                    </Col>
                </Row>
            }
            {/* ARCHIVE */}
            {!hideOptions?.includes('isArchived') &&
                <Row className="mt-2 pb-2 border-bottom">
                    <Col xs={1} align="center" valign="top" className="p-0">
                        <Icon name="archive" color="danger" className="me-2" />
                    </Col>
                    <Col xs={9} className="ps-0">
                        <Text color="danger" bold id="label-for-archived-toggle">
                            Archive for Everyone:
                            <button
                                id="archive-tooltip"
                                className="btn btn-link p-0 ms-1"
                                data-toggle="tooltip"
                                data-tooltip="If you no longer need this room to be active, archive it. It can be unarchived later."
                                aria-label="Info: If you no longer need this room to be active, archive it. It can be unarchived later."
                                data-tooltip-pos="top"
                                role="tooltip"
                            >
                                <Icon
                                    name="circle-info"
                                    color="primary"
                                    aria-hidden={true}
                                />
                            </button>
                        </Text>
                    </Col>
                    <Col align="right">
                        <ToggleSwitch
                            name="isArchived"
                            id="isArchived"
                            onChange={(checked) => onBeforeArchive(checked)}
                            checked={isArchived}
                            aria-labelledby="label-for-archived-toggle"
                        />
                    </Col>
                </Row>
            }
        </Col>
    )
})

export { RoomSettingsOptions }
